import { lazy, Suspense, useCallback, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router';
import type { RouteObject } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { BRANDING, PASSWORD_LINK_PATH } from '@fkwd-qc/shared';
import { PageErrorBoundary } from '@/components/error-boundary';
import { EvidenceDrawer } from '@/components/evidence-drawer';
import { LoadingRows } from '@/components/loading-rows';
import { NavSidebar } from '@/components/nav-sidebar';
import { ProtectedRoute } from '@/components/protected-route';
import { TopBar } from '@/components/top-bar';
import { useSession } from '@/hooks/use-session';
import { ShellActionContext } from '@/hooks/use-shell-action';
import type { ShellActionRegistrar } from '@/hooks/use-shell-action';
import { useShellStatus } from '@/hooks/use-shell-status';
import { MAIN_ELEMENT_ID, NAV_ELEMENT_ID } from '@/lib/landmarks';
import { SESSION_QUERY_KEY } from '@/lib/query-keys';
import { findNavItem, MODULE_ROUTES, MODULES } from '@/modules';

const LoginPage = lazy(() => import('@/modules/auth/login'));
const SetPasswordPage = lazy(() => import('@/modules/auth/set-password'));

/**
 * Sidebar, topbar, the routed view and the evidence drawer, all driven by the module registry.
 *
 * @since 1.0.0
 * @returns the app shell element
 */
function AppShell() {
    const location = useLocation();
    const queryClient = useQueryClient();
    const { user, can } = useSession();
    const { badgeCounts, statusLine, lastEventLabel } = useShellStatus();
    const navItem = findNavItem(location.pathname);
    const registryRef = useRef<Map<string, () => void> | null>(null);
    const [isNavOpen, setIsNavOpen] = useState(false);

    const register = useCallback<ShellActionRegistrar>((actionId, handler) => {
        const registry = (registryRef.current ??= new Map<string, () => void>());
        registry.set(actionId, handler);

        return () => {
            if (registry.get(actionId) === handler) {
                registry.delete(actionId);
            }
        };
    }, []);

    /**
     * Dispatches a topbar button press to whichever view claimed that action id.
     *
     * @since 1.0.0
     * @param actionId - the id declared on the nav item
     * @returns void
     */
    function handleAction(actionId: string): void {
        const handler = registryRef.current?.get(actionId);

        if (handler === undefined) {
            if (import.meta.env.DEV) {
                console.warn(`[fkwd-qc] no view has claimed the topbar action "${actionId}"`);
            }

            return;
        }

        handler();
    }

    /**
     * Refetches every query except the session after a failure, so the retry button retries.
     *
     * @since 1.0.0
     * @returns void
     */
    function handleRetry(): void {
        void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0] });
    }

    /**
     * Opens or closes the sidebar panel on a narrow screen.
     *
     * @since 1.3.0
     * @returns void
     */
    function handleToggleNav(): void {
        setIsNavOpen((previous) => !previous);
    }

    /**
     * Closes the sidebar panel once a link in it has been followed.
     *
     * @since 1.3.0
     * @returns void
     */
    function handleNavigate(): void {
        setIsNavOpen(false);
    }

    // a button whose endpoint the signed-in user cannot call is not shown at all
    const actions = navItem?.actions?.filter((action) => action.minRole === undefined || can(action.minRole));

    return (
        <ShellActionContext.Provider value={register}>
            <a className="fkwd-skip-link" href={`#${MAIN_ELEMENT_ID}`}>
                Skip to main content
            </a>
            <NavSidebar
                badgeCounts={badgeCounts}
                id={NAV_ELEMENT_ID}
                isOpen={isNavOpen}
                lastEventLabel={lastEventLabel}
                modules={MODULES}
                onNavigate={handleNavigate}
                role={user?.role ?? null}
                statusLine={statusLine}
            />
            <main id={MAIN_ELEMENT_ID} tabIndex={-1}>
                <TopBar
                    actions={actions}
                    isNavOpen={isNavOpen}
                    navId={NAV_ELEMENT_ID}
                    onAction={handleAction}
                    onToggleNav={handleToggleNav}
                    title={navItem?.title ?? BRANDING.product.name}
                />
                <div className="fkwd-body">
                    <PageErrorBoundary onRetry={handleRetry} resetKey={location.pathname}>
                        <ProtectedRoute minRole={navItem?.minRole}>
                            <Suspense fallback={<LoadingRows label="Loading view" />}>
                                <Outlet />
                            </Suspense>
                        </ProtectedRoute>
                    </PageErrorBoundary>
                </div>
            </main>
            <EvidenceDrawer />
        </ShellActionContext.Provider>
    );
}

const ROUTES: RouteObject[] = [
    { path: '/login', Component: LoginPage },
    { path: PASSWORD_LINK_PATH, Component: SetPasswordPage },
    {
        element: <ProtectedRoute />,
        children: [{ element: <AppShell />, children: MODULE_ROUTES }],
    },
    { path: '*', element: <Navigate replace to="/" /> },
];

/**
 * The routed application. Routes come from the module registry.
 *
 * @since 1.0.0
 * @returns the routed element tree
 */
export function App() {
    const element = useRoutes(ROUTES);

    return <Suspense fallback={<LoadingRows label="Loading" />}>{element}</Suspense>;
}
